import { Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useXNGDispatch, useXNGSelector } from "../../../../../context/store";
import {
  ACTION_addProviderToProxyCaseload,
  ACTION_addProviderToSupervisedCaseload,
  ACTION_SetProxyCaseload,
  selectLoggedInClientAssignment,
  selectUser,
  setUserResponse,
} from "../../../../../context/slices/userProfileSlice";
import { isAnyMsb } from "../../../../../utils/msb_role";
import { getSizing } from "../../../../sizing";
import { XNGUserCard_0 } from "./UserCard";
import {
  PostAccessRequest,
  ServiceProviderCaseloadOption,
  ServiceProviderRef,
  UserRef,
} from "../../../../../profile-sdk";
import { CaseloadType } from "@xng/xng-backend";
import { DataEntryProviderControl } from "./DataEntryProviderControl";
import { AddAnotherProvider } from "./AddAnotherProvider";
import {
  selectDataEntryProvider,
  setDataEntryProvider,
  setIsSignedInAsDataEntryProvider,
} from "../../../../../context/slices/dataEntryProvider";
import { AssistantProviderControl } from "./AssistantProviderControl";
import { CreateNewProviderModal } from "../../modals/CreateServiceProviderToCaseLoadModal";
import { selectStateInUS } from "../../../../../context/slices/stateInUsSlice";
import { selectClientID } from "../../../../../context/slices/loggedInClientSlice";
import { API_SERVICEPROVIDERS_BACKEND, API_USERS, API_USERS_BACKEND } from "../../../../../api/api";
import { fetchServiceProviderResponse } from "../../../../../api/fetchServiceProviderResponse";
import { fetchUserResponse } from "../../../../../api/fetchUserResponse";
import { providerNotFoundErrorActions } from "../../../../../context/slices/providerNotFoundErrorSlice";
import { placeholderForFutureLogErrorText } from "../../../../../temp/errorText";
import { MainMenuV1 } from "../_main_menu_components";
import { AddServiceProviderToCaseloadModal } from "../../modals/AddServiceProviderToCaseloadModal";
import { useDispatch } from "react-redux";
import { thankYouModalActions } from "../../../../../context/slices/thankYouModalSlice";
import { removeProvderConfirmationModalAction } from "../../../../../context/slices/removeProviderConfirmationModalSlice";
import useRefetchUnpostedSessionsRequests from "../../../../../views/unposted_sessions/hooks/use_refetch_unposted_sessions_requests";

import { useCreateNewProviderModalController } from "../../hooks/use_create_new_provider_modal_controller";
import { useAddProviderToApproverCaseload } from "../../hooks/use_add_provider_to_approver_caseload";
import { useAddProviderToProxyCaseload } from "../../hooks/use_add_provider_to_proxy_caseload";

export default function ProfileSlideContent({
  onClose
}:{
  onClose?: () => void;
}) {
  // Hooks
  const user = useXNGSelector(selectUser);
  const dispatch = useXNGDispatch();
  const serviceProviderOptions = useCaseloadServiceProviders();
  const { setActiveProvider, removeProviderFromApproverCaseload, decRequestPostAccess } =
    useProfileMenuActions();

  // States
  const [showAddToProxyCaseloadModal, setShowAddToProxyCaseloadModal] = useState<boolean>(false);
  const [showAddToApproverCaseloadModal, setShowAddToApproverCaseloadModal] =
    useState<boolean>(false);

  const cnp = useCreateNewProviderModalController();

  // Selectors
  const userClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const signedInDataEntryProvider = useXNGSelector(selectDataEntryProvider);

  const addProviderToApproverCaseload = useAddProviderToApproverCaseload();
  const addProviderToProxyCaseload = useAddProviderToProxyCaseload();

  return (
    <>
      <MainMenuV1.Wrapper>
        <Typography sx={{ marginLeft: getSizing(2), marginY: getSizing(2) }} variant="h6">
          My Profile
        </Typography>
        <Typography sx={{ marginLeft: getSizing(2) }} variant="overline">
          ACCOUNT
        </Typography>
        <XNGUserCard_0
          user={{
            email: user?.emailAddress!,
            firstName: user?.firstName!,
            lastName: user?.lastName!,
          }}
          useNavigationPath={{ path: "/xlogs/my-profile" }}
          onClose={ onClose }
        />
        {userClientAssignment.isProxyDataEntry && (
          <ProxyCaseloadManager
            appointingServiceProviders={[...(userClientAssignment?.appointingServiceProviders ?? [])]?.sort?.((a, b) => {
              return (a?.firstName ?? "")?.localeCompare(b?.firstName ?? "");
            }) ?? []}
            signedInServiceProvider={signedInDataEntryProvider ?? {}}
            onSignIn={(serviceProviderId) => setActiveProvider(serviceProviderId)}
            onSignOut={() => {
              dispatch(setDataEntryProvider(null));
              dispatch(setIsSignedInAsDataEntryProvider(false));
            }}
            onAddProviderClick={() => setShowAddToProxyCaseloadModal(true)}
            onRequestAccess={decRequestPostAccess}
          />
        )}
        {userClientAssignment.isApprover && (
          <ApproverCaseloadManager
            supervisedServiceProviders={[...(userClientAssignment?.supervisedServiceProviders ?? [])]?.sort((a, b) => {
              return (a?.firstName ?? "").localeCompare(b?.firstName ?? "");
            }) ?? []}
            onRemove={(serviceProviderId) => removeProviderFromApproverCaseload(serviceProviderId)}
            onAddProviderClick={() => {
              setShowAddToApproverCaseloadModal(true);
            }}
          />
        )}
      </MainMenuV1.Wrapper>

      {/* MODALS */}
      <AddServiceProviderToCaseloadModal
        open={showAddToProxyCaseloadModal}
        options={serviceProviderOptions.filter(
          (provider) =>
            !userClientAssignment.appointingServiceProviders?.find(
              (DEProvider) => DEProvider.id === provider.id,
            ),
        )}
        onClose={() => setShowAddToProxyCaseloadModal(false)}
        onUserClick={(serviceProvider: ServiceProviderRef) => {
          addProviderToProxyCaseload(serviceProvider);
        }}
        onCreateUserClick={() => cnp.handleOpen("dec")}
      />
      <AddServiceProviderToCaseloadModal
        open={showAddToApproverCaseloadModal}
        options={serviceProviderOptions.filter(
          (provider) =>
            !userClientAssignment.supervisedServiceProviders?.find(
              (assistant) => assistant.id === provider.id,
            ),
        )}
        onClose={() => setShowAddToApproverCaseloadModal(false)}
        onUserClick={(serviceProvider: ServiceProviderRef) =>
          addProviderToApproverCaseload(serviceProvider)
        }
        onCreateUserClick={() => cnp.handleOpen("approver")}
      />
      <CreateNewProviderModal
        open={cnp.isOpen}
        handleClose={() => cnp.handleClose()}
        onSubmit={cnp.handleSubmit}
      />
    </>
  );
}

// ------------ GLOBAL-COMPATIBLE API HOOKS, SEPARATE FROM FILE LATER ------------

function useCaseloadServiceProviders() {
  const state = useXNGSelector(selectStateInUS);
  const loggedInClientId = useXNGSelector(selectClientID);
  const user = useXNGSelector(selectUser);

  const [serviceProviderOptions, setServiceProviderOptions] = useState<
    ServiceProviderCaseloadOption[]
  >([]);

  async function fetchAndSetCaseloadServiceProviders() {
    try {
      const response = await API_SERVICEPROVIDERS_BACKEND.getServiceProviders({
        clientId: loggedInClientId!,
        includeMSBEmployee: isAnyMsb(user),
      });
      const serviceProviders: ServiceProviderCaseloadOption[] = (response ?? []).map((r) => ({
        id: r.id,
        firstName: r.user?.firstName,
        lastName: r.user?.lastName,
        emailAddress: r.user?.emailAddress,
      }));
      serviceProviders.sort((a, b) => (a.firstName ?? "").localeCompare(b.firstName ?? ""));
      setServiceProviderOptions(serviceProviders);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    fetchAndSetCaseloadServiceProviders();
  }, []);

  return serviceProviderOptions;
}

// I'm not sure which of these can be made into global, reusable API hooks and which are contextual
// only to this file, so for now I'll organize it in this hook to make refactoring it easier later.
export function useProfileMenuActions() {
  const dispatch = useXNGDispatch();
  const state = useXNGSelector(selectStateInUS);
  const loggedInClientId = useXNGSelector(selectClientID);
  const user = useXNGSelector(selectUser);
  const userClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const {refetchOnCaseloadAction} = useRefetchUnpostedSessionsRequests()

  async function setActiveProvider(serviceProviderId: string) {
    try {
      const serviceProvider = await fetchServiceProviderResponse(serviceProviderId);
      // Redux variable for ActingServiceProvider should be set to serviceProvider.
      dispatch(setDataEntryProvider(serviceProvider));
      dispatch(setIsSignedInAsDataEntryProvider(true));
    } catch (err) {
      dispatch(
        providerNotFoundErrorActions.ACTION_ShowProviderNotFound({
          show: true,
          errorMsg: (err as Error).message,
        }),
      );
    }
  }

  async function removeProviderFromApproverCaseload(serviceProviderId: string) {
    const spId = userClientAssignment.serviceProviderProfile?.id;
    if (!spId) throw new Error(placeholderForFutureLogErrorText);

    const memberUser = await API_USERS_BACKEND
      .getUserByServiceProvider({
        serviceProviderId,
        clientId: loggedInClientId!,
      });
    if (!memberUser.id) throw new Error(placeholderForFutureLogErrorText);

    const updatedCaseload = await API_SERVICEPROVIDERS_BACKEND
      .deleteCaseload({
        serviceProviderId: spId,
        caseloadType: CaseloadType.NUMBER_1,
        memberId: memberUser.id,
      });

    const mappedProviders: ServiceProviderRef[] = updatedCaseload.map((r) => ({
      id: r.id,
      firstName: r.firstName,
      lastName: r.lastName,
      email: r.email,
    }));

    dispatch(
      ACTION_addProviderToSupervisedCaseload({
        providers: mappedProviders,
        loggedinClientId: loggedInClientId!,
      }),
    );
    dispatch(
      thankYouModalActions.ACTION_ShowThankyouModal({
        show: true,
        text: `This provider has been removed from \nyour profile.`,
      }),
    );

    refetchOnCaseloadAction();
  }

  async function addProviderToProxyCaseload(serviceProvider: ServiceProviderRef | undefined) {
    if (!serviceProvider) return;
    //Patch users ClientAssignment to add ServiceProviderRef to user.AppointingServiceProviders
    dispatch(
      ACTION_addProviderToProxyCaseload({
        loggedinUserId: loggedInClientId ?? "",
        provider: serviceProvider,
        state,
      }),
    );
    dispatch(
      thankYouModalActions.ACTION_ShowThankyouModal({
        show: true,
        text: `You can now access ${serviceProvider.firstName} ${serviceProvider.lastName}'s account 
    by navigating to “My Profile” in the 
    navigation bar. Access to post in their 
    account has been requested.`,
      }),
    );
  }

  async function removeProviderFromProxyCaseload(serviceProviderId: string) {
    const spId = userClientAssignment.serviceProviderProfile?.id;
    if (!spId) throw new Error(placeholderForFutureLogErrorText);

    const memberUser = await API_USERS_BACKEND
      .getUserByServiceProvider({
        serviceProviderId,
        clientId: loggedInClientId!,
      });
    if (!memberUser.id) throw new Error(placeholderForFutureLogErrorText);

    const updatedCaseload = await API_SERVICEPROVIDERS_BACKEND
      .deleteCaseload({
        serviceProviderId: spId,
        caseloadType: CaseloadType.NUMBER_2,
        memberId: memberUser.id,
      });

    const mappedProviders: ServiceProviderRef[] = updatedCaseload.map((r) => ({
      id: r.id,
      firstName: r.firstName,
      lastName: r.lastName,
      email: r.email,
    }));

    dispatch(
      ACTION_SetProxyCaseload({
        providers: mappedProviders,
        loggedinClientId: loggedInClientId!,
      }),
    );
    dispatch(
      thankYouModalActions.ACTION_ShowThankyouModal({
        show: true,
        text: `This provider has been removed from \nyour profile.`,
      }),
    );

    refetchOnCaseloadAction();
  }

  async function decRequestPostAccess(requestedServiceProvider: ServiceProviderRef) {
    try {
      const postAccessRequestBody: PostAccessRequest = {
        requestedServiceProvider,
        requestingUser: {
          id: user?.id,
          firstName: user?.firstName,
          lastName: user?.lastName,
          email: user?.emailAddress,
        } as UserRef,
      };
      const postAccessResponse = await API_USERS.v1UsersRequestProxyAccessToPostPost(
        state,
        postAccessRequestBody,
      );
      dispatch(
        thankYouModalActions.ACTION_ShowThankyouModal({
          show: true,
          text: `Access to post in ${requestedServiceProvider.firstName} ${requestedServiceProvider.lastName}'s account \nhas been re-requested.`,
        }),
      );
      return postAccessResponse;
    } catch (err) {
      console.log("XNG Error - dec request Access Method: ", (err as Error).message);
      console.error("XNG Error - dec request Access Method: ", (err as Error).message);
    }
  }

  return {
    setActiveProvider,
    removeProviderFromApproverCaseload,
    removeProviderFromProxyCaseload,
    decRequestPostAccess,
  };
}

// Local artifact components, organized down here to clear things up. Can likely be separated from the file into the root of this folder, /my-profile 8/11/23

interface IProxyCaseloadManager {
  appointingServiceProviders: ServiceProviderRef[];
  signedInServiceProvider: ServiceProviderRef;
  onSignIn: (serviceProviderId: string) => void;
  onRequestAccess: (requestedProvider: ServiceProviderRef) => void;
  onSignOut: () => void;
  onAddProviderClick: () => void;
}
function ProxyCaseloadManager(props: IProxyCaseloadManager) {
  const dispatch = useDispatch();
  return (
    <>
      <Typography sx={{ marginLeft: getSizing(2) }} variant="overline">
        DATA ENTRY PROVIDERS
      </Typography>
      {props.appointingServiceProviders?.map((asp) => {
        return (
          <DataEntryProviderControl
            key={asp.id}
            firstName={asp?.firstName ?? ""}
            lastName={asp?.lastName ?? ""}
            email={asp?.email ?? ""}
            onSignIn={() => props.onSignIn(asp.id!)}
            onSignOut={() => props.onSignOut()}
            signedIn={props.signedInServiceProvider.id === asp.id}
            onRequestAccess={() => props.onRequestAccess(asp)}
            onRemove={() => {
              dispatch(
                removeProvderConfirmationModalAction.ACTION_ShowModal({
                  show: true,
                  confirmationData: {
                    confirmationText: `Are you sure you would like to remove \n${asp?.firstName} ${asp?.lastName} from your profile?`,
                    providerInformation: {
                      providerId: asp.id || "",
                      caseloadType: "proxyCaseload",
                    },
                  },
                }),
              );
            }}
          />
        );
      })}
      <AddAnotherProvider onClick={() => props.onAddProviderClick()} />
    </>
  );
}

interface IApproverCaseloadManager {
  supervisedServiceProviders: ServiceProviderRef[];
  onRemove: (serviceProviderId: string) => void;
  onAddProviderClick: () => void;
}
function ApproverCaseloadManager(props: IApproverCaseloadManager) {
  const dispatch = useXNGDispatch();
  return (
    <>
      <Typography sx={{ marginLeft: getSizing(2) }} variant="overline">
        ASSISTANTS
      </Typography>
      {props.supervisedServiceProviders?.map((asp, i) => {
        return (
          <AssistantProviderControl
            key={i}
            firstName={asp?.firstName ?? "NULL"}
            lastName={asp?.lastName ?? "NULL"}
            email={asp?.email ?? ""}
            onRemove={() => {
              dispatch(
                removeProvderConfirmationModalAction.ACTION_ShowModal({
                  show: true,
                  confirmationData: {
                    confirmationText: `Are you sure you would like to remove \n${asp?.firstName} ${asp?.lastName} from your profile?`,
                    providerInformation: {
                      providerId: asp.id || "",
                      caseloadType: "approverCaseload",
                    },
                  },
                }),
              );
            }}
          />
        );
      })}
      <AddAnotherProvider onClick={() => props.onAddProviderClick()} />
    </>
  );
}
